import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarPlus, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useKaartToken, GRIJS } from "@/lib/kaartToken";
import { useTaal } from "@/contexts/TaalContext";
import { Button } from "./ui/button";

/** "vrijdag 16 oktober 14:00 tot 15:00" of "woensdag 22 oktober, hele dag". PUUR. */
export function wanneer(rij, taal) {
  const locale = taal === "en" ? "en-GB" : "nl-NL";
  if (rij.hele_dag) {
    const dag = new Date(`${rij.begin_iso}T12:00:00`).toLocaleDateString(locale,
      { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" });
    return `${dag}, ${taal === "en" ? "all day" : "hele dag"}`;
  }
  const opties = { timeZone: rij.tijdzone };
  const begin = new Date(rij.begin_iso);
  const eind = new Date(rij.eind_iso);
  const dag = begin.toLocaleDateString(locale, { ...opties, weekday: "long", day: "numeric", month: "long" });
  const tijd = (d) => d.toLocaleTimeString(locale, { ...opties, hour: "2-digit", minute: "2-digit" });
  return `${dag} ${tijd(begin)} ${taal === "en" ? "to" : "tot"} ${tijd(eind)}`;
}

/**
 * C2a, 15 oktober 2026: een afspraak op glas, en pas na Bevestig in de agenda.
 *
 * Zelfde opbouw als `TaakVoorstel`: de velden komen uit de opgeslagen rij (niet
 * uit de tekst die een model schreef), de kaart krijgt een WF-40-token, en de
 * bevestiging stuurt ALLE velden terug zoals ze op het scherm stonden. De server
 * vergelijkt ze teken voor teken.
 */
export default function AfspraakVoorstel({ voorstelId }) {
  const { t, taal } = useTaal();
  const [rij, setRij] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");
  const { token, vervallen, laatVervallen, reden } = useKaartToken(
    rij?.id ? `${API}/afspraken/${rij.id}/toon` : "", Boolean(rij?.id && !rij.aangemaakt));

  useEffect(() => {
    let levend = true;
    axios.get(`${API}/afspraken/${voorstelId}`)
      .then((r) => { if (levend) setRij(r.data); })
      .catch((e) => { if (levend) setFout(e.response?.data?.detail || t("Dit voorstel kon niet worden geladen")); });
    return () => { levend = false; };
  }, [voorstelId]); // eslint-disable-line react-hooks/exhaustive-deps -- alleen bij een ander voorstel

  const bevestig = async () => {
    setBezig(true);
    setFout("");
    try {
      const r = await axios.post(`${API}/afspraken/${rij.id}/uitvoeren`, {
        titel: rij.titel, begin_iso: rij.begin_iso, eind_iso: rij.eind_iso,
        hele_dag: rij.hele_dag, tijdzone: rij.tijdzone, token,
      });
      setRij(r.data);
      toast.success(t("Afspraak staat in je agenda"));
    } catch (e) {
      const tekst = e.response?.data?.detail || t("Het aanmaken is niet gelukt");
      setFout(tekst);
      laatVervallen(tekst);
    } finally {
      setBezig(false);
    }
  };

  if (!rij) {
    return fout ? <p className="text-xs text-destructive">{fout}</p> : null;
  }
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="afspraak-voorstel">
      <div className="flex items-center gap-2 text-sm font-medium">
        <CalendarPlus className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span data-testid="afspraak-titel">{rij.titel}</span>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="afspraak-wanneer">{wanneer(rij, taal)}</p>
      {rij.aangemaakt ? (
        <p className="text-xs flex items-center gap-1" data-testid="afspraak-gezet">
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
          {t("Staat in je agenda.")}
          {rij.verwijzing && (
            <a href={rij.verwijzing} target="_blank" rel="noreferrer" className="underline ml-1">
              {t("Open in Google Calendar")}
            </a>
          )}
        </p>
      ) : (
        <Button size="sm" onClick={bevestig} disabled={bezig || !token || vervallen}
                className={vervallen ? GRIJS : ""} data-testid="afspraak-bevestig">
          {bezig && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
          {t("Bevestig en zet in mijn agenda")}
        </Button>
      )}
      {(fout || reden) && <p className="text-xs text-destructive">{fout || reden}</p>}
    </div>
  );
}
